import produce from "immer"
import type { ProjectComparisonDto, ProjectDto } from "./../../models/models"
import {
  BOSATLAS_BUNDLE_CLEAR,
  BOSATLAS_BUNDLE_DELETE_COMPLETE,
  BOSATLAS_BUNDLE_LOAD,
  BOSATLAS_BUNDLE_LOAD_LIST,
  BOSATLAS_COMPARISON_DELETE,
  BOSATLAS_COMPARISON_UPDATE,
  BOSATLAS_LOAD,
  BOSATLAS_LOAD_SUGGESTIONS,
  BOSATLAS_PROJECT_DELETE,
  BOSATLAS_PROJECT_UPDATE
} from "./bosatlas.actions"
import type { BosatlasActions, Bundle, IBosatlasState } from "./bosatlas.types"

const bosatlasInitialState: IBosatlasState = {}

export function bosatlasReducer(currentState: IBosatlasState | undefined, action: BosatlasActions): IBosatlasState {
  const state = currentState ?? bosatlasInitialState
  switch (action.type) {
    case BOSATLAS_LOAD: {
      return replaceBosatlas(state, action.payload as IBosatlasState)
    }
    case BOSATLAS_BUNDLE_LOAD: {
      return addOrUpdateBundle(state, action.payload as Bundle)
    }
    case BOSATLAS_BUNDLE_CLEAR: {
      return clearBundle(state)
    }
    case BOSATLAS_BUNDLE_LOAD_LIST: {
      return replaceBundleList(state, action.payload as Bundle[])
    }
    case BOSATLAS_BUNDLE_DELETE_COMPLETE: {
      return removeBundle(state, action.payload as string)
    }
    case BOSATLAS_COMPARISON_DELETE:
      return removeComparison(state, action.payload as ProjectComparisonDto)
    case BOSATLAS_PROJECT_DELETE:
      return removeProject(state, action.payload as ProjectDto)
    case BOSATLAS_COMPARISON_UPDATE:
      return updateComparison(state, action.payload as ProjectComparisonDto)
    case BOSATLAS_PROJECT_UPDATE:
      return updateProject(state, action.payload as ProjectDto)
    case BOSATLAS_LOAD_SUGGESTIONS:
      return setSuggestions(state, action.payload as string[])
    default: {
      return state
    }
  }
}

function replaceBosatlas(currentState: IBosatlasState, bosatlas: IBosatlasState): IBosatlasState {
  return produce(currentState, (draftState: IBosatlasState) => {
    draftState.bundles = bosatlas.bundles
    draftState.projects = bosatlas.projects
    draftState.projectComparisons = bosatlas.projectComparisons
    draftState.sharedProjects = bosatlas.sharedProjects
    draftState.sharedComparisons = bosatlas.sharedComparisons
  })
}

function replaceBundleList(currentState: IBosatlasState, bundles: Bundle[]): IBosatlasState {
  return produce(currentState, (draftState: IBosatlasState) => {
    draftState.bundles = bundles
  })
}

function addOrUpdateBundle(currentState: IBosatlasState, bundle: Bundle): IBosatlasState {
  return produce(currentState, (draftState: IBosatlasState) => {
    if (currentState.bundles) {
      const index = currentState.bundles.findIndex((x) => x.id === bundle.id)
      draftState.bundles = currentState.bundles
      if (index === -1) {
        if (draftState.bundles.length === 6) draftState.bundles.pop()
        draftState.bundles.unshift(bundle)
      } else {
        draftState.bundles[index] = bundle
      }
    }
    draftState.bundle = bundle
  })
}

function removeBundle(currentState: IBosatlasState, id: string): IBosatlasState {
  return produce(currentState, (draftState: IBosatlasState) => {
    if (currentState.bundles) {
      draftState.bundles = currentState.bundles.filter((x) => x.id !== id)
    }
  })
}

function clearBundle(currentState: IBosatlasState): IBosatlasState {
  return produce(currentState, (draftState: IBosatlasState) => {
    draftState.bundle = undefined
  })
}

function removeComparison(currentState: IBosatlasState, comparison: ProjectComparisonDto): IBosatlasState {
  return produce(currentState, (draftState: IBosatlasState) => {
    if (!draftState.projectComparisons) return

    draftState.projectComparisons = draftState.projectComparisons.filter(
      (c: ProjectComparisonDto) => c.id !== comparison.id
    )
    if (draftState.sharedComparisons)
      draftState.sharedComparisons = draftState.sharedComparisons.filter(
        (p: ProjectComparisonDto) => p.id !== comparison.id
      )
  })
}

function updateComparison(currentState: IBosatlasState, comparison: ProjectComparisonDto): IBosatlasState {
  return produce(currentState, (draftState: IBosatlasState) => {
    if (draftState.projectComparisons) {
      draftState.projectComparisons = draftState.projectComparisons.map((c: ProjectComparisonDto) => {
        return c.id === comparison.id ? comparison : c
      })
    }
    if (draftState.bundle?.comparisons) {
      draftState.bundle.comparisons = draftState.bundle.comparisons.map((c: ProjectComparisonDto) => {
        return c.id === comparison.id ? comparison : c
      })
    }
  })
}

function removeProject(currentState: IBosatlasState, project: ProjectDto): IBosatlasState {
  return produce(currentState, (draftState: IBosatlasState) => {
    if (!draftState.projects) return

    draftState.projects = draftState.projects.filter((p: ProjectDto) => p.id !== project.id)
    if (draftState.sharedProjects)
      draftState.sharedProjects = draftState.sharedProjects.filter((p: ProjectDto) => p.id !== project.id)
  })
}

function updateProject(currentState: IBosatlasState, project: ProjectDto): IBosatlasState {
  return produce(currentState, (draftState: IBosatlasState) => {
    if (draftState.projects) {
      draftState.projects = draftState.projects.map((p: ProjectDto) => {
        return p.id === project.id ? project : p
      })
    }
    if (draftState.bundle?.projects) {
      draftState.bundle.projects = draftState.bundle.projects.map((p: ProjectDto) => {
        return p.id === project.id ? project : p
      })
    }
  })
}

function setSuggestions(currentState: IBosatlasState, suggestions: string[]): IBosatlasState {
  return produce(currentState, (draftState: IBosatlasState) => {
    draftState.searchSuggestions = suggestions
  })
}
